import {
  Box,
  Button,
  Collapse,
  Flex,
  Text,
  useColorMode,
  useDisclosure,
  Wrap,
} from '@chakra-ui/react'
import { Meeting_Step_Type_Enum } from '@gql'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import IconTextButton from '@/common/atoms/IconTextButton'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FileIcon,
  HelpIcon,
  MagicIcon,
  NoteIcon,
  PlayIcon,
} from 'src/icons'

// Facilitation sheets of the Université du Nous (#73), licensed CC BY-SA:
// shown at the top of a meeting whose steps treat operational tensions
// (triage) or governance ones. Same on every instance (Yohan, 21/09/2026).
// PDFs live in `public/fiches`; videos and interactive sheets stay on their
// hosts.
interface Sheet {
  key: 'triage' | 'governance' | 'objections'
  stepType: Meeting_Step_Type_Enum
  // The process's colour, the one of its tensions' badge (OPÉ teal, GOUV
  // purple): the pictogram says what the resource is, the colour which process
  colorScheme: 'teal' | 'purple'
  links: {
    labelKey: 'sheet' | 'help' | 'document' | 'video' | 'dynamic'
    href: string
  }[]
}

const sheets: Sheet[] = [
  {
    key: 'triage',
    stepType: Meeting_Step_Type_Enum.Threads,
    colorScheme: 'teal',
    links: [
      { labelKey: 'sheet', href: '/fiches/triage.pdf' },
      { labelKey: 'help', href: '/fiches/aide-triage.pdf' },
      {
        labelKey: 'dynamic',
        href: 'https://www.thinglink.com/scene/1402759323309834243',
      },
      {
        labelKey: 'video',
        href: 'https://noustube.universite-du-nous.org/w/7ZyiS1TCnfvTL4KLeFei9M',
      },
    ],
  },
  {
    key: 'governance',
    stepType: Meeting_Step_Type_Enum.ThreadsGovernance,
    colorScheme: 'purple',
    links: [
      { labelKey: 'sheet', href: '/fiches/gouvernance.pdf' },
      {
        labelKey: 'dynamic',
        href: 'https://www.thinglink.com/scene/1412052742066667523',
      },
      {
        labelKey: 'video',
        href: 'https://noustube.universite-du-nous.org/w/2cqgn41DwT3WM42qky32Vc',
      },
    ],
  },
  // The objections have their own line (#134): the test sheet, the UdN
  // document on objections in consent decision-making, and a video
  {
    key: 'objections',
    stepType: Meeting_Step_Type_Enum.ThreadsGovernance,
    colorScheme: 'purple',
    links: [
      { labelKey: 'sheet', href: '/fiches/test-objections.pdf' },
      { labelKey: 'document', href: '/fiches/objections.pdf' },
      {
        labelKey: 'video',
        href: 'https://noustube.universite-du-nous.org/w/tTyYKSq1nbmjXZ9de2xe6K',
      },
    ],
  },
]

const linkIcons = {
  sheet: FileIcon,
  help: HelpIcon,
  document: NoteIcon,
  video: PlayIcon,
  dynamic: MagicIcon,
}

// Folded or not, remembered per browser: whoever knows the sheets folds them
// once. Storage may be unavailable (private mode): the banner then just opens.
const storageKey = 'meetingFacilitationSheets.folded'
const readFolded = () => {
  try {
    return localStorage.getItem(storageKey) === '1'
  } catch {
    return false
  }
}
const writeFolded = (folded: boolean) => {
  try {
    localStorage.setItem(storageKey, folded ? '1' : '0')
  } catch {
    // Nothing to do: the choice simply won't be remembered
  }
}

interface Props {
  stepTypes: Meeting_Step_Type_Enum[]
}

export default function MeetingFacilitationSheets({ stepTypes }: Props) {
  const { t } = useTranslation()
  const { colorMode } = useColorMode()
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: !readFolded() })
  useEffect(() => writeFolded(!isOpen), [isOpen])

  const shown = sheets.filter((sheet) => stepTypes.includes(sheet.stepType))
  if (shown.length === 0) return null

  return (
    <Box
      mb={16}
      px={4}
      py={3}
      borderRadius="md"
      bg={colorMode === 'light' ? 'gray.50' : 'whiteAlpha.50'}
    >
      <Button
        variant="unstyled"
        size="sm"
        display="flex"
        alignItems="center"
        gap={1}
        onClick={onToggle}
      >
        {isOpen ? (
          <ChevronDownIcon size="1em" />
        ) : (
          <ChevronRightIcon size="1em" />
        )}
        {t('MeetingFacilitationSheets.heading')}
      </Button>

      <Collapse in={isOpen} animateOpacity>
        {shown.map((sheet) => (
          <Flex key={sheet.key} align="center" gap={3} mt={3} wrap="wrap">
            <Text minW="7em" fontWeight="medium">
              {t(`MeetingFacilitationSheets.${sheet.key}`)}
            </Text>
            <Wrap spacing={2}>
              {sheet.links.map((link) => {
                const Icon = linkIcons[link.labelKey]
                return (
                  // A pictogram alone, its meaning in the bubble (Yohan)
                  <IconTextButton
                    key={link.labelKey}
                    as="a"
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t(`MeetingFacilitationSheets.${link.labelKey}`)}
                    icon={<Icon size={18} />}
                    size="sm"
                    isRound
                    colorScheme={sheet.colorScheme}
                  />
                )
              })}
            </Wrap>
          </Flex>
        ))}
        <Text mt={3} fontSize="xs" color="gray.500">
          {t('MeetingFacilitationSheets.credits')}
        </Text>
      </Collapse>
    </Box>
  )
}
